/**
 * Collaboration Spaces
 *
 * A Space is a bounded working context within a workspace. Three types:
 *   personal — owned by one user; full control; can invite guests
 *   team     — tied to an org_unit; members inherit org membership
 *   org      — the whole workspace; governed by workspace_memberships
 *
 * The "space_memberships.role" field grants a baseline set of capabilities
 * that layer on top of (and merge with) explicit RBAC role_assignments.
 */

import type { CapabilityId } from './capabilityIds';

// ── Core enums ─────────────────────────────────────────────────────────────────

export type SpaceType = 'personal' | 'team' | 'org';

export type SpaceMembershipRole = 'owner' | 'admin' | 'member' | 'viewer' | 'guest';

export type SpaceMembershipStatus = 'active' | 'invited' | 'suspended';

// ── Domain objects ─────────────────────────────────────────────────────────────

export interface CollaborationSpace {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  spaceType: SpaceType;
  orgUnitId?: string;
  ownerId?: string;
  allowGuests: boolean;
  settings: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export interface SpaceMembership {
  id: string;
  spaceId: string;
  userId: string;
  role: SpaceMembershipRole;
  status: SpaceMembershipStatus;
  invitedBy?: string;
  invitedAt?: Date;
  acceptedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface SpaceInvitation {
  id: string;
  spaceId: string;
  token: string;
  email?: string;
  role: Exclude<SpaceMembershipRole, 'owner'>;
  invitedBy: string;
  expiresAt: Date;
  usedAt?: Date;
  usedBy?: string;
  createdAt: Date;
}

/** Joined view: space + the calling user's membership role/status */
export interface MySpace extends CollaborationSpace {
  memberRole: SpaceMembershipRole;
  memberStatus: SpaceMembershipStatus;
}

// ── Capability sets by space role ──────────────────────────────────────────────
//
// Space membership role grants a baseline set of capabilities on top of (or
// independently of) any explicit role_assignments in the RBAC system.
// The permission resolver merges both sources — most-permissive wins.

export const SPACE_ROLE_CAPABILITIES: Record<SpaceMembershipRole, ReadonlySet<CapabilityId>> = {
  owner: new Set<CapabilityId>([
    'ITEM_VIEW', 'ITEM_CRUD', 'ITEM_TRIAGE_APPROVE', 'ITEM_TRIAGE_ROUTE',
    'ITEM_TRIAGE_ATTACH_RELATIONSHIPS', 'ITEM_MANAGE_OWNERSHIP',
    'CYCLE_VIEW', 'CYCLE_PARTICIPATE', 'CYCLE_CREATE', 'CYCLE_CONFIGURE', 'CYCLE_ABORT',
    'PHASE_ADVANCE', 'LEGITIMACY_ADVANCE',
    'TRACK_CREATE', 'TRACK_CONFIGURE',
    'CRITERIA_SELECT', 'CRITERIA_LOCK', 'SCALES_DEFINE', 'REOPEN_CRITERIA',
    'VALUE_ENTER', 'VALUE_ACCEPT', 'VALUE_REJECT', 'VALUES_CONVERGE',
    'VALUE_RANGE_DEFINE', 'VALUE_RANGE_APPROVE',
    'POOL_SELECT', 'POOL_LOCK', 'REOPEN_POOL',
    'SESSION_CREATE', 'SESSION_RUN', 'SESSION_PARTICIPATE',
    'RANK_RUN', 'RESULTS_VIEW', 'OVERRIDE_APPLY', 'RANK_OVERRIDE',
    'CATCHBALL_REQUEST_ISSUE', 'CATCHBALL_PROPOSAL_SUBMIT', 'CATCHBALL_RETURN',
    'CATCHBALL_ACCEPT_PROVISIONAL', 'CATCHBALL_ACCEPT', 'CATCHBALL_REJECT',
    'ADMIN_MANAGE_USERS', 'ADMIN_MANAGE_ROLES', 'ADMIN_MANAGE_PERMISSIONS',
    'ADMIN_GRANT_WORKSPACE',
    'WORKFLOW_CREATE', 'WORKFLOW_EDIT', 'WORKFLOW_DELETE', 'WORKFLOW_ARCHIVE',
    'WORKFLOW_SET_DEFAULT', 'WORKFLOW_ASSIGN', 'WORKFLOW_USE', 'WORKFLOW_MANAGE_TEMPLATES',
    'TASK_ASSIGN', 'TASK_SELF_ASSIGN',
  ]),

  admin: new Set<CapabilityId>([
    'ITEM_VIEW', 'ITEM_CRUD', 'ITEM_TRIAGE_APPROVE', 'ITEM_TRIAGE_ROUTE',
    'ITEM_TRIAGE_ATTACH_RELATIONSHIPS', 'ITEM_MANAGE_OWNERSHIP',
    'CYCLE_VIEW', 'CYCLE_PARTICIPATE', 'CYCLE_CREATE', 'CYCLE_CONFIGURE', 'CYCLE_ABORT',
    'PHASE_ADVANCE', 'LEGITIMACY_ADVANCE',
    'TRACK_CREATE', 'TRACK_CONFIGURE',
    'CRITERIA_SELECT', 'CRITERIA_LOCK', 'SCALES_DEFINE', 'REOPEN_CRITERIA',
    'VALUE_ENTER', 'VALUE_ACCEPT', 'VALUE_REJECT', 'VALUES_CONVERGE',
    'VALUE_RANGE_DEFINE', 'VALUE_RANGE_APPROVE',
    'POOL_SELECT', 'POOL_LOCK', 'REOPEN_POOL',
    'SESSION_CREATE', 'SESSION_RUN', 'SESSION_PARTICIPATE',
    'RANK_RUN', 'RESULTS_VIEW',
    'CATCHBALL_REQUEST_ISSUE', 'CATCHBALL_PROPOSAL_SUBMIT', 'CATCHBALL_RETURN',
    'CATCHBALL_ACCEPT_PROVISIONAL', 'CATCHBALL_ACCEPT', 'CATCHBALL_REJECT',
    'ADMIN_MANAGE_USERS',
    'WORKFLOW_CREATE', 'WORKFLOW_EDIT', 'WORKFLOW_ASSIGN', 'WORKFLOW_USE',
    'WORKFLOW_MANAGE_TEMPLATES',
    'TASK_ASSIGN', 'TASK_SELF_ASSIGN',
  ]),

  member: new Set<CapabilityId>([
    'ITEM_VIEW', 'ITEM_CRUD',
    'CYCLE_VIEW', 'CYCLE_PARTICIPATE',
    'CRITERIA_SELECT',
    'VALUE_ENTER', 'VALUE_ACCEPT', 'VALUE_REJECT', 'VALUES_CONVERGE',
    'POOL_SELECT',
    'SESSION_PARTICIPATE',
    'RESULTS_VIEW',
    'CATCHBALL_REQUEST_ISSUE', 'CATCHBALL_PROPOSAL_SUBMIT', 'CATCHBALL_RETURN',
    'CATCHBALL_ACCEPT_PROVISIONAL', 'CATCHBALL_ACCEPT', 'CATCHBALL_REJECT',
    'WORKFLOW_USE',
    'TASK_SELF_ASSIGN',
  ]),

  viewer: new Set<CapabilityId>([
    'ITEM_VIEW',
    'CYCLE_VIEW',
    'RESULTS_VIEW',
  ]),

  guest: new Set<CapabilityId>([
    'ITEM_VIEW',
    'CYCLE_VIEW',
  ]),
};

/** Returns true when the given space membership role grants the capability. */
export function spaceRoleGrantsCapability(
  role: SpaceMembershipRole,
  capability: CapabilityId
): boolean {
  return (SPACE_ROLE_CAPABILITIES[role] as Set<CapabilityId>).has(capability);
}

// ── Labels ─────────────────────────────────────────────────────────────────────

export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  personal: 'Personal',
  team: 'Team',
  org: 'Organization',
};

export const SPACE_ROLE_LABELS: Record<SpaceMembershipRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
  guest: 'Guest',
};

export const SPACE_ROLE_DESCRIPTIONS: Record<SpaceMembershipRole, string> = {
  owner: 'Full control — create, configure, and manage this space and its members',
  admin: 'Manage content and members, but cannot delete the space',
  member: 'Create and edit content, participate in cycles and sessions',
  viewer: 'Read-only access to items and cycle results',
  guest: 'Limited view access — ideal for external collaborators',
};
